// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/growth-score/components/visuals.tsx (ADAPT)
// ADAPTED (plan P4-6): `BrandMark` read TP's `assets/logos.json` (Shapermint-sample logos by brand name); it now takes the
// logo URL our own data gave (a CI competitor's `logoUrl`, the prospect's brand guide), drawn through `safeHttpUrl`, with
// the initials as the fallback. `BrandFaces` takes those brands. `KvTable`'s default head says "Rank", not "Rank of 17".
// Our `Avatar` / `Table` live at `@/components/ui/*`.
/**
 * The Growth Score's small parts: dimension icon and dot, score bars and
 * pills, brand marks and the Check / You / Cohort tables.
 *
 * Dimension colours are small accents only (a dot, an icon, a radar point);
 * every number and label stays in text tokens.
 */
import { Clapperboard, Filter, Mail, Megaphone, Network, Search, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/shadcn/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { MAX_SCORE, MIN_SCORE, SCORE_REFERENCE } from '@/contracts/onboarding-session'
import { dsVar, type DsTokenPath } from '@/design-system/tokens'
import { safeHttpUrl } from '@/lib/safeHttpUrl'
import { cn } from '@/lib/utils'
import { useProgress } from './motion'
import { barTone, scoreTone } from '../growth-score-model'
import type { DimensionKey, KvRow } from '../growth-score.types'

export const DIMENSION_LOOK: Record<DimensionKey, { icon: LucideIcon; color: DsTokenPath }> = {
  ads: { icon: Megaphone, color: 'chart.1' },
  funnel: { icon: Filter, color: 'chart.2' },
  tiktok: { icon: Clapperboard, color: 'chart.10' },
  search: { icon: Search, color: 'chart.7' },
  email: { icon: Mail, color: 'chart.11' },
  omni: { icon: Network, color: 'color.accent-violet-text' },
}

/** The dimension's icon on a quiet tile, tinted with its colour. */
export function DimensionIcon({ dimension, icon, className }: { dimension?: DimensionKey; icon?: LucideIcon; className?: string }) {
  const Icon = icon ?? (dimension ? DIMENSION_LOOK[dimension].icon : Search)
  return (
    <span
      aria-hidden
      className={cn('flex size-8 shrink-0 items-center justify-center rounded-md bg-ds-ink-a05', className)}
      style={dimension ? { color: dsVar(DIMENSION_LOOK[dimension].color) } : undefined}
    >
      <Icon className="size-4" />
    </span>
  )
}

export function DimensionDot({ dimension }: { dimension: DimensionKey }) {
  return <span aria-hidden className="size-2 shrink-0 rounded-full" style={{ background: dsVar(DIMENSION_LOOK[dimension].color) }} />
}

const BAR_FILL: Record<'bad' | 'warn' | 'good' | 'neutral', DsTokenPath> = {
  bad: 'color.dot-error',
  warn: 'color.dot-warning',
  good: 'color.dot-success',
  neutral: 'color.text-3',
}

/**
 * A bar on the score scale with a tick at the reference, the cohort median. `tone` colours the fill
 * by the score; neutral otherwise.
 */
export function ScoreBar({
  value,
  size = 'sm',
  toned = false,
  className,
  reveal = false,
  delay = 0,
}: {
  value: number
  size?: 'sm' | 'lg'
  toned?: boolean
  className?: string
  /** Fill from empty on mount. */
  reveal?: boolean
  delay?: number
}) {
  const fill = toned ? BAR_FILL[barTone(value)] : size === 'lg' ? 'color.text-2' : BAR_FILL.neutral
  const progress = useProgress(1100, delay, reveal)
  return (
    <div
      role="meter"
      aria-valuemin={MIN_SCORE}
      aria-valuemax={MAX_SCORE}
      aria-valuenow={value}
      aria-label={`${value} out of ${MAX_SCORE}; the cohort median is ${SCORE_REFERENCE}`}
      className={cn('relative w-full rounded-full bg-ds-ink-a05', size === 'lg' ? 'h-2' : 'h-1', className)}
    >
      <span className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${(Math.max(MIN_SCORE, Math.min(MAX_SCORE, value)) / MAX_SCORE) * 100 * progress}%`, background: dsVar(fill) }} />
      <span
        aria-hidden
        className={cn('absolute w-0.5 -translate-x-1/2 rounded-full bg-ds-text-2', size === 'lg' ? '-top-1 h-4' : '-top-1 h-3')}
        style={{ left: `${(SCORE_REFERENCE / MAX_SCORE) * 100}%` }}
      />
    </div>
  )
}

/** "40 / 100", tinted below the median; "No score yet" (or the signal's label) when there's no score. */
export function ScorePill({ score, label }: { score: number | null; label?: string }) {
  if (score == null) return <Badge intent="neutral">{label ?? 'No score yet'}</Badge>
  const tone = scoreTone(score)
  return <Badge intent={tone === 'bad' ? 'error' : tone === 'warn' ? 'warning' : 'neutral'}>{score} / {MAX_SCORE}</Badge>
}

export function YesNo({ yes }: { yes: boolean }) {
  return <Badge intent={yes ? 'success' : 'error'}>{yes ? 'Yes' : 'No'}</Badge>
}

/** "40 / 100" as a figure: the number in the metric face, the scale quiet. `reveal` counts it up on mount. */
export function ScoreFigure({ value, size = 'md', reveal = false, delay = 0 }: { value: number; size?: 'md' | 'lg' | 'xl'; reveal?: boolean; delay?: number }) {
  const progress = useProgress(1300, delay, reveal)
  return (
    <span className="inline-flex items-baseline gap-1">
      <span className={size === 'xl' ? 'text-title-greeting text-foreground' : size === 'lg' ? 'text-metric text-foreground' : 'text-metric-sm text-foreground'}>
        {Math.round(value * progress)}
      </span>
      <span className="text-label text-ds-text-2">/ {MAX_SCORE}</span>
    </span>
  )
}

const initials = (name: string) =>
  name
    .split(/[\s&]+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

/** A brand's 32px logo (the URL our data gave, http(s) only), or its initials when we have none or it does not load. */
export function BrandMark({ name, logoUrl, size = 'md', className }: { name: string; logoUrl?: string | null; size?: 'sm' | 'md'; className?: string }) {
  const logo = safeHttpUrl(logoUrl)
  return (
    <Avatar kind="person" size={size === 'sm' ? 'sm' : 'md'} title={name} className={cn('shrink-0 border border-ds-hairline bg-ds-paper', className)}>
      {logo ? <AvatarImage src={logo} alt="" referrerPolicy="no-referrer" loading="lazy" /> : null}
      <AvatarFallback className="text-label text-ds-text-2">{initials(name)}</AvatarFallback>
    </Avatar>
  )
}

export interface FaceBrand {
  name: string
  logoUrl?: string | null
}

/** Overlapping brand marks: the cohort at a glance. */
export function BrandFaces({ brands, size = 'md', label }: { brands: FaceBrand[]; size?: 'sm' | 'md'; label: string }) {
  return (
    <div role="img" aria-label={label} className="flex items-center">
      {brands.map((b, i) => (
        <BrandMark key={`${b.name}-${i}`} name={b.name} logoUrl={b.logoUrl} size={size} className={cn(i > 0 && '-ml-2', 'ring-2 ring-background')} />
      ))}
    </div>
  )
}

/** A Check / You / Cohort median / Rank table: the value in "You" reads strongest, the median quietest. */
export function KvTable({
  rows,
  head = ['Check', 'You', 'Cohort median', 'Rank'],
  caption,
}: {
  rows: (KvRow | [ReactNode, ReactNode, ReactNode, ReactNode])[]
  head?: [string, string, string, string]
  caption?: string
}) {
  return (
    <Table aria-label={caption}>
      <TableHeader>
        <TableRow>
          {head.map((h) => (
            <TableHead key={h}>{h}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={i}>
            <TableCell>{r[0]}</TableCell>
            <TableCell className="text-tab text-foreground">{r[1]}</TableCell>
            <TableCell className="text-ds-text-2">{r[2]}</TableCell>
            <TableCell>{r[3]}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

/** A check's name over what it measures, for table cells. */
export function CheckName({ name, sub }: { name: string; sub?: string }) {
  return (
    <div className="flex flex-col">
      <span>{name}</span>
      {sub ? <span className="text-label text-ds-text-3">{sub}</span> : null}
    </div>
  )
}
